import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { CreateExternalManipulationUnitRequestDTO, ExternalManipulationUnitDTO } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { ColWithGap } from '@components/styles.tsx';
import { InputText } from 'primereact/inputtext';
import { findValidationMessage } from '@utils/validation.tsx';
import InputSKU from '@components/ringil3/Input/InputSKU/InputSKU.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  onContinue: () => void;
  item?: ExternalManipulationUnitDTO;
}

const initialValues: CreateExternalManipulationUnitRequestDTO = {
  externalId: null,
  name: null,
  unitWeight: null,
  unitsOnPallet: null,
  unitsInBox: null,
  boxOnPallet: null,
  boxWeight: null,
  palletWeight: null,
  cargoTemplateCode: null,
};

export const useVolumetricItemSidebar = () => {
  const [visible, setVisible] = useState(false);

  const VolumetricItemSidebar: React.FC<ComponentProps> = ({ onContinue, item }: ComponentProps) => {
    const { tr } = useTranslationLgs();
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const { createExternalManipulationUnit, updateExternalManipulationUnit } = useContext(ApiContext);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [loading, setLoading] = useState(false);

    const schema = yup.object().shape({
      externalId: yup.string().required(),
      name: yup.string().nullable(),
      unitWeight: yup.number().required(),
      unitsOnPallet: yup.number().required(),
      unitsInBox: yup.number().required(),
      boxOnPallet: yup.number().required(),
      boxWeight: yup.number().required(),
      palletWeight: yup.number().required(),
      cargoTemplateCode: yup.string().required(),
    });

    const getInitialFormValues = (item?: ExternalManipulationUnitDTO): CreateExternalManipulationUnitRequestDTO => {
      if (!item) return initialValues;
      return {
        externalId: item.externalId ?? '',
        name: item.name ?? '',
        unitWeight: item.unitWeight ?? null,
        unitsOnPallet: item.unitsOnPallet ?? null,
        unitsInBox: item.unitsInBox ?? null,
        boxOnPallet: item.boxOnPallet ?? null,
        boxWeight: item.boxWeight ?? null,
        palletWeight: item.palletWeight ?? null,
        cargoTemplateCode: item.cargoTemplateCode ?? '',
      };
    };

    const form = useForm<CreateExternalManipulationUnitRequestDTO>(
      schema,
      getInitialFormValues(item),
      (d) => onContinueHandler(d),
      false,
      false,
      () => {
      },
      'none',
    );

    const onContinueHandler = (data: CreateExternalManipulationUnitRequestDTO) => {
      setLoading(true);
      if (item) {
        return updateExternalManipulationUnit(item.id, data,
          () => {
            setVisible(false);
            setLoading(false);
            onContinue();
          },
          null,
          {
            onValidationFailed: (errors) => {
              setLoading(false);
              setBeValidationErrors(errors);
            },
          });
      } else {
        createExternalManipulationUnit(data,
          () => {
            setVisible(false);
            setLoading(false);
            onContinue();
          },
          null,
          {
            onValidationFailed: (errors) => {
              setLoading(false);
              setBeValidationErrors(errors);
            },
          });
      }
    };

    if (!visible) return null;

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => {
            form.validateAndSend();
          }}
          heading={tr(`VolumetricItemSidebar.heading`, `Volumetric item`)}
          loading={loading}>

          <ColWithGap>
            <BackendValidationComponent />
            <Field
              label={tr('VolumetricItemSidebar.formExternalId', 'External ID')}
              required={!form.values.externalId}
              errorMessage={findValidationMessage(form.validationErrors, form.names.externalId, tr)}
            >
              <InputText
                value={form.values.externalId}
                onChange={e => form.setFieldValue(form.names.externalId, e.target.value)}
                placeholder={tr('VolumetricItemSidebar.formPlaceholderExternalId', 'Enter external ID')}
              />
            </Field>
            <Field
              label={tr('VolumetricItemSidebar.formName', 'Name')}
              required={false}
              errorMessage={findValidationMessage(form.validationErrors, form.names.name, tr)}
            >
              <InputText
                value={form.values.name}
                onChange={e => form.setFieldValue(form.names.name, e.target.value)}
                placeholder={tr('VolumetricItemSidebar.formPlaceholderName', 'Enter name')}
              />
            </Field>
            <Field
              label={tr('VolumetricItemSidebar.formUnitWeight', 'Unit weight')}
              required={!form.values.unitWeight}
              errorMessage={findValidationMessage(form.validationErrors, form.names.unitWeight, tr)}
            >
              <InputText
                value={form.values.unitWeight}
                onChange={e => form.setFieldValue(form.names.unitWeight, e.target.value)}
                placeholder={tr('VolumetricItemSidebar.formPlaceholderUnitWeight', 'Enter unit weight')}
                type="number"
              />
            </Field>
            <Field
              label={tr('VolumetricItemSidebar.formUnitsOnPallet', 'Units on pallet')}
              required={!form.values.unitsOnPallet}
              errorMessage={findValidationMessage(form.validationErrors, form.names.unitsOnPallet, tr)}
            >
              <InputText
                value={form.values.unitsOnPallet}
                onChange={e => form.setFieldValue(form.names.unitsOnPallet, e.target.value)}
                placeholder={tr('VolumetricItemSidebar.formPlaceholderUnitsOnPallet', 'Enter units on pallet')}
                type="number"
              />
            </Field>
            <Field
              label={tr('VolumetricItemSidebar.formUnitsInBox', 'Units in box')}
              required={!form.values.unitsInBox}
              errorMessage={findValidationMessage(form.validationErrors, form.names.unitsInBox, tr)}
            >
              <InputText
                value={form.values.unitsInBox}
                onChange={e => form.setFieldValue(form.names.unitsInBox, e.target.value)}
                placeholder={tr('VolumetricItemSidebar.formPlaceholderUnitsInBox', 'Enter units in box')}
                type="number"
              />
            </Field>
            <Field
              label={tr('VolumetricItemSidebar.formBoxOnPallet', 'Boxes on pallet')}
              required={!form.values.boxOnPallet}
              errorMessage={findValidationMessage(form.validationErrors, form.names.boxOnPallet, tr)}
            >
              <InputText
                value={form.values.boxOnPallet}
                onChange={e => form.setFieldValue(form.names.boxOnPallet, e.target.value)}
                placeholder={tr('VolumetricItemSidebar.formPlaceholderBoxOnPallet', 'Enter boxes on pallet')}
                type="number"
              />
            </Field>
            <Field
              label={tr('VolumetricItemSidebar.formBoxWeight', 'Box weight')}
              required={!form.values.boxWeight}
              errorMessage={findValidationMessage(form.validationErrors, form.names.boxWeight, tr)}
            >
              <InputText
                value={form.values.boxWeight}
                onChange={e => form.setFieldValue(form.names.boxWeight, e.target.value)}
                placeholder={tr('VolumetricItemSidebar.formPlaceholderBoxWeight', 'Enter box weight')}
                type="number"
              />
            </Field>
            <Field
              label={tr('VolumetricItemSidebar.formPalletWeight', 'Pallet weight')}
              required={!form.values.palletWeight}
              errorMessage={findValidationMessage(form.validationErrors, form.names.palletWeight, tr)}
            >
              <InputText
                value={form.values.palletWeight}
                onChange={e => form.setFieldValue(form.names.palletWeight, e.target.value)}
                placeholder={tr('VolumetricItemSidebar.formPlaceholderPalletWeight', 'Enter pallet weight')}
                type="number"
              />
            </Field>
            <Field
              label={tr('VolumetricItemSidebar.formCargoTemplateCode', 'Cargo template code')}
              required={!form.values.cargoTemplateCode}
              errorMessage={findValidationMessage(form.validationErrors, form.names.cargoTemplateCode, tr)}
            >
              <InputSKU
                fullWidth
                value={form.values.cargoTemplateCode}
                companyId={loggedUser.companyId}
                onChange={v => {
                  form.setFieldValue(form.names.cargoTemplateCode, v);
                }}
              />
            </Field>
          </ColWithGap>
        </RingilSidebar>
      </>);

  };

  return { VolumetricItemSidebar, setVisible };
};